import React, { useState, useEffect, useRef } from 'react';
import { auditAPI, utentiAPI } from '../services/api';
import ErroreLettura from '../components/comuni/ErroreLettura';
import { conUnTentativo, motivoDellaLettura } from '../utils/primaLettura';
import '../styles/RegistroAccessi.css';

/**
 * RegistroModifiche — schermata riservata agli admin.
 *
 * Mostra chi ha cambiato cosa e permette di annullare una modifica.
 *
 * L'annullamento e' esplicito su COSA tornera' indietro: non un
 * generico "annulla" ma "riporta telefono a 3474823797". Un pulsante
 * che non dice cosa fa, su dati clinici, e' una trappola.
 *
 * Il database rifiuta comunque l'annullamento se il campo e' stato
 * cambiato ancora dopo: la salvaguardia non dipende da questa pagina.
 */

const messaggioErrore = (e, ripiego) => e?.message || e?.response?.data?.message || ripiego;

const dataOra = (v) =>
    v ? new Date(v).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const ETICHETTE_OPERAZIONE = {
    INSERT: 'Creazione',
    UPDATE: 'Modifica',
    DELETE: 'Cancellazione',
};

const FILTRI_VUOTI = { tabella: '', record_id: '', utente_id: '', operazione: '', dal: '', al: '' };

/* Pausa fra l'ultima scelta in un menu e la ricerca. Ogni ricerca lascia una
   riga nel registro degli accessi (senza entita_id, quindi il quarto d'ora non
   le raggruppa) e sorvegliaRegistri ne conta il volume per utente: tre scelte
   di fila devono essere UNA ricerca. Stessa ragione della barra comandi
   ("i pazienti si cercano per parola, non per lettera", CLAUDE.md). */
const PAUSA_MENU_MS = 600;

/** Valore leggibile: i vuoti vanno detti, non lasciati bianchi. */
const mostraValore = (v) => {
    if (v === null || v === undefined || v === '') return '(vuoto)';
    if (typeof v === 'boolean') return v ? 'sì' : 'no';
    const s = String(v);
    return s.length > 80 ? s.slice(0, 80) + '…' : s;
};

const RegistroModifiche = () => {
    const [modifiche, setModifiche] = useState([]);
    const [tabelle, setTabelle] = useState({});
    const [utenti, setUtenti] = useState([]);
    const [filtri, setFiltri] = useState(FILTRI_VUOTI);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);
    const [inCorso, setInCorso] = useState(null);

    /* Conta le ricerche partite: una risposta lenta non scrive sopra quella
       di una ricerca piu' nuova (con i menu che cercano da soli, due
       ricerche in volo sono normali). */
    const ultimaRicerca = useRef(0);

    /* I filtri piu' recenti e la ricerca in pausa: due scelte nello stesso
       istante non devono partire da un render vecchio. */
    const filtriRecenti = useRef(FILTRI_VUOTI);
    const inPausa = useRef(null);
    const annullaPausa = () => {
        if (inPausa.current) { clearTimeout(inPausa.current); inPausa.current = null; }
    };

    /* L'errore della lettura sta per conto suo, con «Riprova»: la lettura
       riuscita lo toglie, quello di un annullamento resta in `errore`
       (primaLettura.js). */
    const [erroreLettura, setErroreLettura] = useState(null);
    const carica = async (f = filtri) => {
        const questa = ++ultimaRicerca.current;
        try {
            setCaricamento(true);
            setErrore(null);
            const puliti = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ''));
            const r = await conUnTentativo(() => auditAPI.modifiche(puliti));
            if (questa !== ultimaRicerca.current) return;
            setModifiche(r.data?.data?.modifiche || []);
            setTabelle(r.data?.data?.tabelle || {});
            setErroreLettura(null);
        } catch (e) {
            if (questa !== ultimaRicerca.current) return;
            setErroreLettura(motivoDellaLettura(e, 'Impossibile caricare il registro'));
        } finally {
            if (questa === ultimaRicerca.current) setCaricamento(false);
        }
    };

    useEffect(() => () => annullaPausa(), []);

    useEffect(() => {
        carica(FILTRI_VUOTI);
        conUnTentativo(() => utentiAPI.getAll())
            .then((r) => setUtenti(r.data?.data?.utenti || []))
            .catch(() => {});
        // eslint-disable-next-line
    }, []);

    const imposta = (nuovi) => {
        filtriRecenti.current = nuovi;
        setFiltri(nuovi);
    };

    const aggiorna = (campo, valore) => imposta({ ...filtriRecenti.current, [campo]: valore });

    /* Rilievo del beta, 14/10/2026: i filtri si leggevano solo al clic su
       «Cerca». Chi sceglieva «Modifica» o scriveva l'id e premeva Invio
       vedeva l'elenco di prima, coi suoi «Riporta», e il filtro sembrava
       rotto (il server lo applicava gia'). Un menu cerca da solo, ma dopo
       una pausa dall'ultima scelta (PAUSA_MENU_MS: tre scelte di fila sono
       una ricerca e una riga nel registro degli accessi, come nella barra
       comandi); l'id e le date si scrivono e si cercano con Invio o con
       «Cerca», che partono subito e annullano la ricerca in pausa. */
    const scegli = (campo, valore) => {
        aggiorna(campo, valore);
        annullaPausa();
        inPausa.current = setTimeout(() => {
            inPausa.current = null;
            carica(filtriRecenti.current);
        }, PAUSA_MENU_MS);
    };

    const cerca = (e) => {
        e.preventDefault();
        annullaPausa();
        carica(filtriRecenti.current);
    };

    const azzera = () => {
        annullaPausa();
        imposta(FILTRI_VUOTI);
        carica(FILTRI_VUOTI);
    };

    /* `_riga` non e' un campo: e' il nome della riga delle tabelle con
       chiave di testo (impostazioni e metodi di pagamento, migration 259). */
    const campiDi = (m) => {
        if (!m.cambiamenti) return [];
        return Object.entries(m.cambiamenti).filter(([campo]) => campo !== '_riga');
    };

    const ripristina = async (m) => {
        const campi = campiDi(m)
            .map(([campo, v]) => `  ${campo} → ${mostraValore(v.prima)}`)
            .join('\n');

        if (!window.confirm(`Riportare indietro questi campi?\n\n${campi}\n\nL'operazione resta registrata.`)) return;

        setErrore(null);
        setAvviso(null);
        try {
            setInCorso(m.id);
            await auditAPI.ripristinaModifica(m.id);
            setAvviso('Modifica annullata.');
            carica();
        } catch (e) {
            setErrore(messaggioErrore(e, 'Errore nell\'annullamento'));
        } finally {
            setInCorso(null);
        }
    };

    return (
        <div className="ra-wrap">
            <div className="ra-testa">
                <div>
                    <h1>Registro modifiche</h1>
                    <p className="ra-sottotitolo">
                        Chi ha cambiato cosa, con il valore precedente. Le modifiche possono
                        essere annullate, purché nessuno sia intervenuto nel frattempo sullo
                        stesso campo.
                    </p>
                </div>
            </div>

            <form className="ra-filtri" data-non-trattiene onSubmit={cerca}>
                <div className="ra-campo">
                    <label htmlFor="tabella">Tipo di dato</label>
                    <select id="tabella" value={filtri.tabella}
                            onChange={(e) => scegli('tabella', e.target.value)}>
                        <option value="">tutti</option>
                        {Object.entries(tabelle).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                        ))}
                    </select>
                </div>

                <div className="ra-campo">
                    <label htmlFor="record_id">Id record</label>
                    <input id="record_id" type="number" value={filtri.record_id}
                           onChange={(e) => aggiorna('record_id', e.target.value)}
                           placeholder="es. 10" />
                </div>

                <div className="ra-campo">
                    <label htmlFor="utente_id">Utente</label>
                    <select id="utente_id" value={filtri.utente_id}
                            onChange={(e) => scegli('utente_id', e.target.value)}>
                        <option value="">tutti</option>
                        {utenti.map((u) => (
                            <option key={u.id} value={u.id}>{u.cognome} {u.nome}</option>
                        ))}
                    </select>
                </div>

                <div className="ra-campo">
                    <label htmlFor="operazione">Operazione</label>
                    <select id="operazione" value={filtri.operazione}
                            onChange={(e) => scegli('operazione', e.target.value)}>
                        <option value="">tutte</option>
                        {Object.entries(ETICHETTE_OPERAZIONE).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                        ))}
                    </select>
                </div>

                <div className="ra-campo">
                    <label htmlFor="dal">Dal</label>
                    <input id="dal" type="date" value={filtri.dal}
                           onChange={(e) => aggiorna('dal', e.target.value)} />
                </div>

                <div className="ra-campo">
                    <label htmlFor="al">Al</label>
                    <input id="al" type="date" value={filtri.al}
                           onChange={(e) => aggiorna('al', e.target.value)} />
                </div>

                <div className="ra-filtri-azioni">
                    <button type="button" className="ra-btn-secondario" onClick={azzera}>Azzera</button>
                    <button type="submit" className="ra-btn-primario" disabled={caricamento}>
                        {caricamento ? 'Ricerca…' : 'Cerca'}
                    </button>
                </div>
            </form>

            {filtri.record_id !== '' && filtri.tabella === '' && (
                <p className="ra-sottotitolo">
                    L'id vale in ogni tipo di dato: lo stesso numero compare in più tipi
                    (paziente, visita, fattura…). Per trovare una sola riga scegli anche
                    il «Tipo di dato».
                </p>
            )}

            <ErroreLettura testo={erroreLettura} onRiprova={() => carica(filtriRecenti.current)}
                classe="ra-errore" Contenitore="div" />
            {errore && <div className="ra-errore">{errore}</div>}
            {avviso && <div className="ra-avviso">{avviso}</div>}

            {caricamento ? (
                <div className="ra-stato">Caricamento…</div>
            ) : modifiche.length === 0 ? (
                <div className="ra-vuoto">Nessuna modifica corrisponde ai filtri.</div>
            ) : (
                <div className="rm-elenco">
                    {modifiche.map((m) => {
                        const campi = campiDi(m);
                        /* Impostazioni e metodi di pagamento non si ripristinano da
                           qui (non hanno un id): si rimettono da «Dati fiscali».
                           Nemmeno le fatture e le loro righe (Alex, 2/10/2026): il
                           ripristino cambierebbe un documento fiscale senza dirlo
                           al Sistema TS; lo rifiuta anche il server. */
                        const annullabile = m.operazione === 'UPDATE' && !m.ripristinato_il && campi.length > 0
                            && !['parametri', 'metodi_pagamento', 'fatture', 'fattura_righe'].includes(m.tabella);

                        return (
                            <div key={m.id} className={`rm-voce ${m.ripristinato_il ? 'rm-voce-annullata' : ''}`}>
                                <div className="rm-voce-testa">
                                    <div>
                                        <span className={`ra-azione ra-azione-${m.operazione.toLowerCase()}`}>
                                            {ETICHETTE_OPERAZIONE[m.operazione] || m.operazione}
                                        </span>
                                        <span className="rm-oggetto">
                                            {tabelle[m.tabella] || m.tabella}
                                            {m.record_id ? ` #${m.record_id}` : ''}
                                            {m.cambiamenti?._riga ? ` «${m.cambiamenti._riga}»` : ''}
                                        </span>
                                    </div>
                                    <span className="ra-quando">{dataOra(m.created_at)}</span>
                                </div>

                                <div className="rm-autore">
                                    {m.utente_nome || <span className="ra-ignoto">modifica non attribuita a un utente</span>}
                                </div>

                                {campi.length > 0 && (
                                    <table className="rm-campi">
                                        <tbody>
                                            {campi.map(([campo, v]) => (
                                                <tr key={campo}>
                                                    <td className="rm-campo-nome">{campo}</td>
                                                    <td className="rm-campo-prima">{mostraValore(v.prima)}</td>
                                                    <td className="rm-freccia">→</td>
                                                    <td className="rm-campo-dopo">{mostraValore(v.dopo)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}

                                {m.operazione === 'DELETE' && m.tabella === 'visita_prestazioni' && (
                                    <p className="rm-nota">
                                        Prestazione tolta dalla visita, o sostituita da un risalvataggio: non si rimette
                                        da qui. Se l'ha tolta «Togli», chi e il motivo stanno nella riga «Prestazione
                                        tolta dalla visita».
                                    </p>
                                )}
                                {m.operazione === 'DELETE' && m.tabella !== 'visita_prestazioni' && (
                                    <p className="rm-nota">
                                        Cancellazione: il ripristino avviene dal cestino, non da qui.
                                    </p>
                                )}

                                <div className="rm-voce-piede">
                                    {m.ripristinato_il ? (
                                        <span className="rm-annullata">
                                            Annullata il {dataOra(m.ripristinato_il)}
                                            {m.ripristinato_da_cognome ? ` da ${m.ripristinato_da_cognome}` : ''}
                                        </span>
                                    ) : annullabile ? (
                                        <button
                                            className="ra-btn-secondario"
                                            onClick={() => ripristina(m)}
                                            disabled={inCorso === m.id}
                                        >
                                            {inCorso === m.id
                                                ? 'Annullamento…'
                                                : `Riporta ${campi.map(([c]) => c).join(', ')} al valore precedente`}
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <p className="ra-piede">
                Il registro mostra le ultime 100 righe corrispondenti ai filtri. Anche gli
                annullamenti vengono registrati: la storia delle modifiche non viene mai riscritta.
            </p>
        </div>
    );
};

export default RegistroModifiche;
